import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";

import { clx } from "../../../../../../utils/Helpers";
import Upload from "../../../../../../components/Upload";
import {
  PROOF_OF_TRANSACTION_MAX_BYTES,
} from "./constants";
import useProofOfTransactionUpload from "./useProofOfTransactionUpload";
import "./EventPayrollProofOfTransaction.scss";

const EventPayrollProofOfTransaction = ({
  labels,
  disabled,
  fullWidth,
  requiredError,
  uploadFile,
  onReadyChange,
  onUploadComplete,
  onFileClear,
  onValidationChange,
  onUploadAttemptWithoutFile,
}) => {
  const [uploadError, setUploadError] = useState(null);

  const upload = useProofOfTransactionUpload({
    uploadFile,
    onUploadComplete,
    onFileClear,
    onUploadError: () => setUploadError(labels.uploadFailed),
  });

  useEffect(() => {
    onReadyChange?.(upload.isReady);
  }, [onReadyChange, upload.isReady]);

  useEffect(() => {
    onValidationChange?.(Boolean(uploadError));
  }, [onValidationChange, uploadError]);

  useEffect(() => {
    if (!requiredError) return;
    setUploadError(null);
  }, [requiredError]);

  const displayError = requiredError || uploadError;

  const uploadArea = (
    <div className="event-payroll-proof-of-transaction__dropzone">
      <div className="event-payroll-proof-of-transaction__icon-wrap">
        <CloudUploadOutlined className="event-payroll-proof-of-transaction__icon" />
      </div>
      <p className="event-payroll-proof-of-transaction__line mb-0 text-center">
        <span className="event-payroll-proof-of-transaction__action">
          {labels.clickToUpload}
        </span>
        {labels.dragHint ? (
          <span className="event-payroll-proof-of-transaction__rest">
            {" "}
            {labels.dragHint}
          </span>
        ) : null}
      </p>
      {labels.hint && (
        <p className="event-payroll-proof-of-transaction__hint mb-0 text-center">
          {labels.hint}
        </p>
      )}
    </div>
  );

  return (
    <Upload
      id="proof-of-transaction-upload"
      name="proofOfTransaction"
      className={clx(
        "event-payroll-proof-of-transaction w-100",
        fullWidth && "event-payroll-proof-of-transaction--full",
      )}
      wrapperClasses="w-100"
      isPdf
      isImage
      accept="application/pdf,image/jpeg,image/png,image/jpg"
      maxFileSizeBytes={PROOF_OF_TRANSACTION_MAX_BYTES}
      invalidTypeMessage={labels.invalidType}
      invalidSizeMessage={labels.invalidSize}
      uploadArea={uploadArea}
      img={upload.preview}
      pdf={upload.file}
      onChange={upload.handleChange}
      onChangeError={(message) => {
        setUploadError(message || null);
      }}
      onPickerDismissedWithoutFile={onUploadAttemptWithoutFile}
      isInputHasErr={Boolean(displayError)}
      errorMsg={displayError || undefined}
      disabled={disabled}
      isUploading={upload.isUploading}
      uploadProgress={upload.isUploading ? upload.progress : undefined}
      uploadProgressLabel={
        upload.isUploading ? `${upload.progress}%` : undefined
      }
      showUploadProgress
    />
  );
};

EventPayrollProofOfTransaction.propTypes = {
  labels: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  requiredError: PropTypes.string,
  uploadFile: PropTypes.func.isRequired,
  onReadyChange: PropTypes.func,
  onUploadComplete: PropTypes.func,
  onFileClear: PropTypes.func,
  onValidationChange: PropTypes.func,
  onUploadAttemptWithoutFile: PropTypes.func,
};

EventPayrollProofOfTransaction.defaultProps = {
  disabled: false,
  fullWidth: false,
  requiredError: null,
  onReadyChange: undefined,
  onUploadComplete: undefined,
  onFileClear: undefined,
  onValidationChange: undefined,
  onUploadAttemptWithoutFile: undefined,
};

export default EventPayrollProofOfTransaction;
